/**
 * TimePicker — compact 12h type-to-fill input. Replaces native <input type="time">
 * to match the brand chrome; same data contract: value/onChange are 24h "HH:MM".
 */

import React, { useEffect, useRef, useState } from 'react';
import { FiClock } from 'react-icons/fi';

const pad = (n) => String(n).padStart(2, '0');
const onlyDigits = (s) => (s || '').replace(/\D+/g, '').slice(0, 2);

// "HH:MM" 24h -> { hour: '01'..'12', minute: '00'..'59', period: 'AM'|'PM' }
function parse24(value) {
  if (!value || typeof value !== 'string' || !value.includes(':')) {
    return { hour: '', minute: '', period: 'AM' };
  }
  const [hStr, mStr] = value.split(':');
  const h24 = Math.max(0, Math.min(23, parseInt(hStr, 10)));
  const minute = Math.max(0, Math.min(59, parseInt(mStr, 10)));
  if (Number.isNaN(h24) || Number.isNaN(minute)) {
    return { hour: '', minute: '', period: 'AM' };
  }
  const period = h24 >= 12 ? 'PM' : 'AM';
  let hour12 = h24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour: pad(hour12), minute: pad(minute), period };
}

// { hour, minute, period } -> 24-hour "HH:MM" (or '' if incomplete)
function to24({ hour, minute, period }) {
  const h12 = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (Number.isNaN(h12) || Number.isNaN(m)) return '';
  if (h12 < 1 || h12 > 12 || m < 0 || m > 59) return '';
  let h24 = h12 % 12;
  if (period === 'PM') h24 += 12;
  return `${pad(h24)}:${pad(m)}`;
}

export default function TimePicker({
  value = '',
  onChange,
  disabled = false,
  className = '',
}) {
  // Local state so partial typing isn't reset from upstream 24h on every keystroke.
  const initial = parse24(value);
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [period, setPeriod] = useState(initial.period);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  // Latest-value refs — the single SYNCHRONOUS source of truth for hour/minute/period.
  // React state can lag a synchronous callback: typing the 2nd hour digit calls
  // minuteRef.focus() below, which fires the hour field's blur BEFORE setHour flushes.
  // Reading the render closure there gave handleHourBlur a stale single digit, which
  // Math.max(1,…) collapsed to "01" (every legal hour starts with 0 or 1). Reading
  // these refs instead keeps every callback consistent. Always written via the
  // setXValue helpers, alongside setState.
  const hourValRef = useRef(initial.hour);
  const minuteValRef = useRef(initial.minute);
  const periodValRef = useRef(initial.period);

  const setHourValue = (v) => { hourValRef.current = v; setHour(v); };
  const setMinuteValue = (v) => { minuteValRef.current = v; setMinute(v); };
  const setPeriodValue = (v) => { periodValRef.current = v; setPeriod(v); };

  // Sync from upstream (e.g. Restore Draft), but not while user is mid-edit.
  useEffect(() => {
    const upstream = parse24(value);
    if (
      document.activeElement === hourRef.current ||
      document.activeElement === minuteRef.current
    ) return;
    setHourValue(upstream.hour);
    setMinuteValue(upstream.minute);
    setPeriodValue(upstream.period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (next = {}) => {
    // Read latest values from refs (never the stale render closure), then emit only
    // when both fields parse — avoids clobbering saved state mid-edit.
    const combined = to24({
      hour: hourValRef.current,
      minute: minuteValRef.current,
      period: periodValRef.current,
      ...next,
    });
    if (combined) onChange?.(combined);
  };

  const handleHourChange = (raw) => {
    const v = onlyDigits(raw);
    setHourValue(v);
    // Auto-jump to minute on 2 digits. The ref was written above, so the blur this
    // focus() synchronously triggers reads the full value — not a stale single digit.
    if (v.length === 2 && minuteRef.current) {
      minuteRef.current.focus();
      minuteRef.current.select();
    }
    commit({ hour: v });
  };

  const handleMinuteChange = (raw) => {
    const v = onlyDigits(raw);
    setMinuteValue(v);
    commit({ minute: v });
  };

  const handleHourBlur = () => {
    const cur = hourValRef.current;
    if (!cur) return; // empty stays empty — never coerce to a default
    const h = parseInt(cur, 10);
    if (Number.isNaN(h)) { setHourValue(''); return; }
    const padded = pad(Math.max(1, Math.min(12, h)));
    setHourValue(padded);
    commit({ hour: padded });
  };

  const handleMinuteBlur = () => {
    const cur = minuteValRef.current;
    if (!cur) return; // empty stays empty
    const m = parseInt(cur, 10);
    if (Number.isNaN(m)) { setMinuteValue(''); return; }
    const padded = pad(Math.max(0, Math.min(59, m)));
    setMinuteValue(padded);
    commit({ minute: padded });
  };

  const togglePeriod = () => {
    if (disabled) return;
    const next = periodValRef.current === 'AM' ? 'PM' : 'AM';
    setPeriodValue(next);
    commit({ period: next });
  };

  const handleKeyDown = (e, which) => {
    // Backspace in an empty minute field jumps back to hour.
    if (which === 'minute' && e.key === 'Backspace' && !minute) {
      e.preventDefault();
      hourRef.current?.focus();
      return;
    }
    // ':' or '/' as a quick way to jump from hour → minute.
    if (which === 'hour' && (e.key === ':' || e.key === '/' || e.key === ' ')) {
      e.preventDefault();
      minuteRef.current?.focus();
      minuteRef.current?.select();
    }
  };

  return (
    <div
      className={`flex items-center justify-between gap-2 border-[1.5px] rounded-[8px] px-[11px] py-2 bg-white transition-colors focus-within:border-[#1e8a8a] ${disabled ? 'opacity-60' : ''} ${className}`}
      style={{ borderColor: '#ddd6c6' }}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <FiClock className="text-[12px] text-[#7a7060] shrink-0" />

        <div className="flex items-center" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        <input
          ref={hourRef}
          type="text"
          inputMode="numeric"
          maxLength={2}
          disabled={disabled}
          value={hour}
          onChange={(e) => handleHourChange(e.target.value)}
          onBlur={handleHourBlur}
          onKeyDown={(e) => handleKeyDown(e, 'hour')}
          onFocus={(e) => e.target.select()}
          placeholder="HH"
          aria-label="Hour"
          className="w-[20px] text-center text-[12.5px] bg-transparent outline-none text-[#08111f] placeholder:text-[#c0b8a8]"
        />
        <span className="text-[12.5px] text-[#7a7060]">:</span>
        <input
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          maxLength={2}
          disabled={disabled}
          value={minute}
          onChange={(e) => handleMinuteChange(e.target.value)}
          onBlur={handleMinuteBlur}
          onKeyDown={(e) => handleKeyDown(e, 'minute')}
          onFocus={(e) => e.target.select()}
          placeholder="MM"
          aria-label="Minute"
          className="w-[20px] text-center text-[12.5px] bg-transparent outline-none text-[#08111f] placeholder:text-[#c0b8a8]"
        />
        </div>
      </div>

      <button
        type="button"
        onClick={togglePeriod}
        disabled={disabled}
        title={`Switch to ${period === 'AM' ? 'PM' : 'AM'}`}
        aria-label={`Period: ${period}, click to toggle`}
        className="text-[10px] font-bold uppercase tracking-[0.04em] px-1.5 py-[2px] rounded-[5px] border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          background: period === 'AM' ? '#fff7e6' : '#145f5f18',
          color: period === 'AM' ? '#8a6a14' : '#145f5f',
          borderColor: period === 'AM' ? '#f3dfa8' : '#145f5f40',
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.background = period === 'AM' ? '#fff0d6' : '#145f5f28';
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.currentTarget.style.background = period === 'AM' ? '#fff7e6' : '#145f5f18';
        }}
      >
        {period}
      </button>
    </div>
  );
}
